import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Helpers from "../Config/Helpers";
import ReusableDataGrid from "../Components/ReusableDataGrid";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Chip,
  IconButton,
  Stack,
  CircularProgress,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PeopleIcon from "@mui/icons-material/People";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EditIcon from "@mui/icons-material/Edit";

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [perms, setPerms] = useState([]);

  const [receipts, setReceipts] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [recentReceipts, setRecentReceipts] = useState([]);
  const [recentEstimates, setRecentEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const isInvoiceReceipt = (receipt) =>
    String(receipt?.receiptType || "").toLowerCase() === "invoice";
  const isEstimateReceipt = (receipt) => !isInvoiceReceipt(receipt);
  const toLocalDateString = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(() =>
    toLocalDateString(new Date()),
  );

  const getData = () => {
    if (!Helpers.authParentId) {
      setLoading(false);
      return;
    }

    if (!selectedDate) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const tzOffsetMinutes = new Date().getTimezoneOffset();
    axios
      .get(
        `${Helpers.baseUrl}/reports/daily/${Helpers.authParentId}?date=${selectedDate}&tzOffsetMinutes=${tzOffsetMinutes}`,
        Helpers.headers,
      )
      .then((response) => {
        if (!response?.data) {
          return;
        }

        const payload = response.data;
        const report = payload?.report || payload?.data || payload;

        const allReceipts = Array.isArray(report?.receipts)
          ? [...report.receipts].filter(isInvoiceReceipt).reverse()
          : [];
        const allEstimates = Array.isArray(report?.receipts)
          ? [...report.receipts].filter(isEstimateReceipt).reverse()
          : [];
        const allCustomers = Array.isArray(report?.customers)
          ? [...report.customers].reverse()
          : [];
        const allVehicles = Array.isArray(report?.vehicles)
          ? [...report.vehicles].reverse()
          : [];

        setReceipts(allReceipts);
        setEstimates(allEstimates);
        setCustomers(allCustomers);
        setVehicles(allVehicles);
        setRecentCustomers(allCustomers.slice(0, 10));
        setRecentReceipts(allReceipts.slice(0, 10));
        setRecentEstimates(allEstimates.slice(0, 10));
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const calcPrice = (item) => {
    let total = 0;
    for (let index = 0; index < item.length; index++) {
      total += item[index].totalPrice || 0;
    }
    return total.toFixed(2);
  };

  const getPerms = () => {
    const link = location.pathname;
    const permissions = JSON.parse(localStorage.getItem("permissions"));
    if (permissions) {
      const perm = permissions.find(
        (permission) => permission.tab_link === link,
      );
      setPerms(perm || {});
    }
  };

  useEffect(() => {
    getData();
    getPerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, selectedDate]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const today = toLocalDateString(new Date());
      setSelectedDate((prev) => (prev === today ? prev : today));
    }, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = String(dateString).split("-").map(Number);
    const date =
      Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)
        ? new Date(year, month - 1, day)
        : new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const statCards = [
    {
      title: "Customers",
      value: customers.length,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: "#667eea",
      bgColor: "rgba(102, 126, 234, 0.1)",
    },
    {
      title: "Vehicles",
      value: vehicles.length,
      icon: <DirectionsCarIcon sx={{ fontSize: 40 }} />,
      color: "#f093fb",
      bgColor: "rgba(240, 147, 251, 0.1)",
    },
    {
      title: "Receipts",
      value: receipts.length,
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
      color: "#4facfe",
      bgColor: "rgba(79, 172, 254, 0.1)",
    },
    {
      title: "Total Sales",
      value: `$${calcPrice(receipts)}`,
      icon: <AttachMoneyIcon sx={{ fontSize: 40 }} />,
      color: "#43e97b",
      bgColor: "rgba(67, 233, 123, 0.1)",
    },
  ];

  // Receipts columns with SAFETY CHECKS
  const receiptColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "customerName",
      headerName: "Customer",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => {
        const safeRow = row || value?.row || {};
        return safeRow.customer?.name || "N/A";
      },
    },
    {
      field: "vehicleInfo",
      headerName: "Vehicle",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => {
        const safeRow = row || value?.row || {};
        return safeRow.vehicle
          ? `${safeRow.vehicle.make} ${safeRow.vehicle.model}`
          : "N/A";
      },
    },
    {
      field: "totalPrice",
      headerName: "Total Price",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueGetter: (value, row) => {
        const safeRow = row || value?.row || {};
        return Number(safeRow.totalPrice || 0);
      },
      valueFormatter: (value) => {
        const raw =
          typeof value === "object" && value !== null ? value.value : value;
        return `$${Number(raw || 0).toFixed(2)}`;
      },
    },
    {
      field: "paid",
      headerName: "Amount Paid",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueGetter: (value, row) => {
        const safeRow = row || value?.row || {};
        return Number(safeRow.paid || 0);
      },
      valueFormatter: (value) => {
        const raw =
          typeof value === "object" && value !== null ? value.value : value;
        return `$${Number(raw || 0).toFixed(2)}`;
      },
    },
    {
      field: "remaining",
      headerName: "Remaining",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueGetter: (value, row) => {
        const safeRow = row || value?.row || {};
        return Number(safeRow.remaining || 0);
      },
      valueFormatter: (value) => {
        const raw =
          typeof value === "object" && value !== null ? value.value : value;
        return `$${Number(raw || 0).toFixed(2)}`;
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        if (!params || !params.row || !params.value) return null;
        return (
          <Chip
            label={params.value}
            size="small"
            color={params.value === "Paid" ? "success" : "warning"}
            sx={{ fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params || !params.row || !params.row._id) return null;
        return (
          <IconButton
            component={Link}
            to={`/user/receipts/receipt-details/${params.row._id}`}
            color="success"
            size="small"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  // Customers columns (no valueGetter, so should be fine)
  const estimateColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "customerName",
      headerName: "Customer",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => {
        const safeRow = row || value?.row || {};
        return safeRow.customer?.name || "N/A";
      },
    },
    {
      field: "vehicleInfo",
      headerName: "Vehicle",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => {
        const safeRow = row || value?.row || {};
        return safeRow.vehicle
          ? `${safeRow.vehicle.make} ${safeRow.vehicle.model}`
          : "N/A";
      },
    },
    {
      field: "totalPrice",
      headerName: "Estimate",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueGetter: (value, row) => {
        const safeRow = row || value?.row || {};
        return Number(safeRow.totalPrice || 0);
      },
      valueFormatter: (value) => {
        const raw =
          typeof value === "object" && value !== null ? value.value : value;
        return `$${Number(raw || 0).toFixed(2)}`;
      },
    },
    {
      field: "status",
      headerName: "Type",
      width: 120,
      renderCell: () => (
        <Chip
          label="Estimate"
          size="small"
          color="info"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params || !params.row || !params.row._id) return null;
        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {(perms?.can_update === 1 || Helpers.authUser.user_role == null) && (
              <IconButton
                component={Link}
                to="/user/receipts/add"
                state={{ receiptToEdit: params.row }}
                color="primary"
                size="small"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              component={Link}
              to={`/user/receipts/receipt-details/${params.row._id}`}
              color="success"
              size="small"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  // Customers columns (no valueGetter, so should be fine)
  const customerColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "name",
      headerName: "Customer Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 140,
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 200,
    },
  ];

  // Prepare rows for DataGrids
  const receiptRows = recentReceipts.map((receipt, index) => ({
    ...receipt,
    id: receipt._id,
    serial: index + 1,
  }));

  const estimateRows = recentEstimates.map((estimate, index) => ({
    ...estimate,
    id: estimate._id,
    serial: index + 1,
  }));

  const customerRows = recentCustomers.map((customer, index) => ({
    ...customer,
    id: customer._id,
    serial: index + 1,
  }));

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: 3,
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "100% !important",
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "300px",
              height: "300px",
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "50%",
              transform: "translate(30%, -30%)",
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                fontSize="small"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              />
            }
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Dashboard
            </Typography>
          </Breadcrumbs>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: 0.5,
                  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                Dashboard Overview
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  fontSize: "0.95rem",
                }}
              >
                Daily Report for {formatDate(selectedDate)}
              </Typography>
            </Box>
            <Chip
              icon={<TrendingUpIcon sx={{ color: "white !important" }} />}
              label="Live Data"
              size="small"
              sx={{
                background: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
                color: "white",
                fontWeight: 600,
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            />
          </Box>
        </Paper>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        {stat.title}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: stat.color,
                        }}
                      >
                        {loading ? (
                          <CircularProgress
                            size={24}
                            sx={{ color: stat.color }}
                          />
                        ) : (
                          stat.value
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        on {formatDate(selectedDate)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: stat.bgColor,
                        color: stat.color,
                      }}
                    >
                      {stat.icon}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={1}
          sx={{
            mb: 3,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2.5,
              borderBottom: 1,
              borderColor: "divider",
              background: "linear-gradient(to right, #f8f9fa 0%, #ffffff 100%)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ReceiptIcon sx={{ mr: 1, color: "#667eea" }} />
                Today's Receipts ({receipts.length})
              </Typography>
              {loading && <CircularProgress size={20} />}
            </Stack>
          </Box>
          <Box sx={{ p: 2, minHeight: 260 }}>
            <ReusableDataGrid
              rows={receiptRows}
              columns={receiptColumns}
              loading={loading}
              pageSize={10}
              pageSizeOptions={[10, 25, 50]}
              noRowsMessage="No receipts available to display"
              autoHeight={true}
              sx={{
                height: receiptRows.length === 0 && !loading ? 260 : "auto",
                minHeight: 220,
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "rgba(102, 126, 234, 0.04)",
                },
                "& .MuiDataGrid-overlay": {
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                },
              }}
            />
          </Box>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            mb: 3,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2.5,
              borderBottom: 1,
              borderColor: "divider",
              background: "linear-gradient(to right, #f8f9fa 0%, #ffffff 100%)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ReceiptIcon sx={{ mr: 1, color: "#f59e0b" }} />
                Today's Estimates ({estimates.length})
              </Typography>
              {loading && <CircularProgress size={20} />}
            </Stack>
          </Box>
          <Box sx={{ p: 2, minHeight: 260 }}>
            <ReusableDataGrid
              rows={estimateRows}
              columns={estimateColumns}
              loading={loading}
              pageSize={10}
              pageSizeOptions={[10, 25, 50]}
              noRowsMessage="No estimates available to display"
              autoHeight={true}
              sx={{
                height: estimateRows.length === 0 && !loading ? 260 : "auto",
                minHeight: 220,
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "rgba(245, 158, 11, 0.05)",
                },
                "& .MuiDataGrid-overlay": {
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                },
              }}
            />
          </Box>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            mb: 6,
          }}
        >
          <Box
            sx={{
              p: 2.5,
              borderBottom: 1,
              borderColor: "divider",
              background: "linear-gradient(to right, #f8f9fa 0%, #ffffff 100%)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <PeopleIcon sx={{ mr: 1, color: "#667eea" }} />
                Today's Customers ({customers.length})
              </Typography>
              {loading && <CircularProgress size={20} />}
            </Stack>
          </Box>
          <Box sx={{ p: 2, minHeight: 260 }}>
            <ReusableDataGrid
              rows={customerRows}
              columns={customerColumns}
              loading={loading}
              pageSize={10}
              pageSizeOptions={[10, 25, 50]}
              noRowsMessage="No customers available to display"
              autoHeight={true}
              sx={{
                height: customerRows.length === 0 && !loading ? 260 : "auto",
                minHeight: 220,
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "rgba(102, 126, 234, 0.04)",
                },
                "& .MuiDataGrid-overlay": {
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                },
              }}
            />
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default Home;
